import type { FmDataDisplayRule } from '../../../types/fm-data-value';
import type { FmMapLegendLevel } from '../mapLegendPalette';
import type { FmStoryAction, FmStoryChart } from './elementFields.types';
import type {
  FmMapSlotProps,
  FmPrimaryMapSlotProps,
  FmRankedSpatialMapSlotProps,
  GovernedMapSlotKey,
  MeasurementMapSlotKey,
  PreviewMapSlotKey,
  WorkspacePrimaryMapSlotKey,
  REPORT_RANKED_SPATIAL_MAP_SLOT_KEY,
} from '@/shared/config/mapSlotRegistry';
import type { ContextualForestMediaRequest } from '@/shared/config/contextualForestMedia';

/** Media slot shapes: governed maps, registry images and charts with their chrome. */
export type FmStoryMapMediaLayoutPreset =
  | 'media-right'
  | 'media-left'
  | 'stacked'
  | 'full-width-with-callout-rail'
  | 'report-snapshot'
  | 'timeline-focus';

type FmStoryPrimaryMapSlotKey = WorkspacePrimaryMapSlotKey | MeasurementMapSlotKey;
type FmStoryRankedSpatialMapSlotKey = typeof REPORT_RANKED_SPATIAL_MAP_SLOT_KEY;
type FmStoryStandardMapSlotKey = Exclude<
  GovernedMapSlotKey,
  FmStoryPrimaryMapSlotKey | FmStoryRankedSpatialMapSlotKey
>;

export type FmStoryPrimaryLiveMapFigure = {
  kind: 'live';
  slotKey: FmStoryPrimaryMapSlotKey;
  slotProps: FmPrimaryMapSlotProps;
};
export type FmStoryStandardLiveMapFigure = {
  kind: 'live';
  slotKey: FmStoryStandardMapSlotKey | PreviewMapSlotKey;
  slotProps?: FmMapSlotProps;
};
export type FmStoryRankedSpatialLiveMapFigure = {
  kind: 'live';
  slotKey: FmStoryRankedSpatialMapSlotKey;
  slotProps: FmRankedSpatialMapSlotProps;
};
export type FmStoryLiveMapFigure =
  | FmStoryPrimaryLiveMapFigure
  | FmStoryRankedSpatialLiveMapFigure
  | FmStoryStandardLiveMapFigure;

export type FmStoryMapMediaFigure =
  | { kind: 'asset'; assetKey: string; altOverride?: string }
  | FmStoryLiveMapFigure;

export interface FmStoryMapMediaCallout {
  id: string;
  label: string;
  value?: string | number;
  unit?: string;
  description?: string;
  source?: string;
  tone?: 'highlight' | 'warning' | 'success' | 'neutral';
}

export interface FmStoryMapMediaStep {
  id: string;
  label: string;
  sublabel?: string;
  disabled?: boolean;
  testId?: string;
}

export interface FmStoryMapMediaLegendBin {
  id: string;
  label?: string;
  min?: number;
  max?: number;
  level: FmMapLegendLevel;
}

/**
 * Map furniture the species palette does not own, each drawn by a registered
 * renderer in its own swatch. A legend entry naming something the map never
 * paints in that colour would be a legend that lies.
 */
export const FM_STORY_MAP_LEGEND_FEATURES = [
  'property-boundary',
  'access-route',
  'thinning',
  'seed-tree',
  'clearcut',
  'planting',
  'tending',
  'untouched',
] as const;
export type FmStoryMapLegendFeature = (typeof FM_STORY_MAP_LEGEND_FEATURES)[number];

/**
 * One named thing on the map. A species entry takes its swatch from the shared
 * chart palette by code, so a species is the same colour on the map, in the
 * charts and here; a feature entry names map furniture.
 */
export type FmStoryMapLegendCategory =
  | { id: string; label: string; speciesCode: number; feature?: never }
  | { id: string; label: string; feature: FmStoryMapLegendFeature; speciesCode?: never };

export interface FmStoryMapMediaLegend {
  title?: string;
  units?: string;
  scale?: string;
  paletteName?: string;
  bins?: FmStoryMapMediaLegendBin[];
  thresholds?: number[];
  noDataLabel?: string;
  /** Categorical entries (species, operations, furniture) instead of a value scale. */
  categories?: readonly FmStoryMapLegendCategory[];
}

export interface FmStoryMapMediaSource {
  label: string;
  timestamp?: string;
  licenseLabel?: string;
}

interface FmStoryMapLikeMediaSlot {
  kind: 'map' | 'active-content';
  figure?: FmStoryMapMediaFigure;
  layoutPreset?: FmStoryMapMediaLayoutPreset;
  intro?: string;
  steps?: FmStoryMapMediaStep[];
  selectedStepId?: string;
  onStepChange?: (stepId: string) => void;
  stepControl?: 'segmented' | 'radio';
  stepLabel?: string;
  callouts?: FmStoryMapMediaCallout[];
  selectedCalloutId?: string;
  onSelectCallout?: (id: string) => void;
  legend?: FmStoryMapMediaLegend;
  source?: FmStoryMapMediaSource;
  interactionHint?: string;
  loading?: boolean;
  errorLabel?: string;
  emptyLabel?: string;
  reveal?: boolean;
  testId?: string;
}

/** One feature the owner may put in or take out of a multi-selection. */
export interface FmStoryMapSelectableFeature {
  id: string;
  /** The feature's name as the owner reads it, e.g. "Stand 12". */
  label: string;
  /** One short fact that tells features apart in the list, e.g. "Thinning 2027 · 1.8 ha". */
  detail?: string;
}

/**
 * Multi-select mode of the map element: the owner builds a SET of features by
 * clicking them, drawing a lasso, or ticking them in the list under the map
 * (the keyboard path). Select all and select none act on `features`, never on
 * features the map shows but the owner may not choose.
 */
export interface FmStoryMapMultiSelection {
  features: readonly [FmStoryMapSelectableFeature, ...FmStoryMapSelectableFeature[]];
  selectedIds: readonly string[];
  onSelectionChange: (selectedIds: readonly string[]) => void;
  labels: {
    /** Accessible name of the feature list. */
    list: string;
    /** Carries `{selected}` and `{total}`. */
    count: string;
    selectAll: string;
    selectNone: string;
    /** Arms the lasso. */
    lasso: string;
    /** Shown while the lasso is armed: what to do next. */
    lassoActive: string;
  };
}

interface FmStoryImageMediaSlotBase {
  kind: 'image';
  altOverride?: string;
  caption?: string;
  credit?: string;
  source?: FmStoryMapMediaSource;
  emptyLabel?: string;
  testId?: string;
}

export type FmStoryImageMediaSlot = FmStoryImageMediaSlotBase &
  (
    | { assetKey: string; mediaContext?: never }
    | { assetKey?: never; mediaContext: ContextualForestMediaRequest }
  );

export interface FmStoryChartMediaSlot {
  kind: 'chart';
  chart: FmStoryChart;
  /** Locale used by the renderer for numeric axes and tooltips. */
  locale?: string;
  title: string;
  caption: string;
  unit: string;
  takeaway: string;
  explanationTitle?: string;
  contextDetail?: string;
  source?: string;
  valueKind?: FmDataDisplayRule['kind'];
  emptyLabel?: string;
  testId?: string;
}

export type FmStoryMediaSlot =
  | FmStoryMapLikeMediaSlot
  | FmStoryImageMediaSlot
  | FmStoryChartMediaSlot;

export type FmStoryActionIconKey = 'currency' | 'clock' | 'calendar';

export interface FmStoryActionSetItem {
  key: string;
  label: string;
  description: string;
  tags?: string[];
  iconKey?: FmStoryActionIconKey;
  media?: FmStoryMediaSlot;
  status?: 'available' | 'active' | 'denied' | 'pending' | 'unavailable';
  statusLabel?: string;
  disabledReason?: string;
  cta: FmStoryAction;
  testId?: string;
}

export interface FmStoryActionSet {
  presentation?: 'switcher' | 'cards';
  items: FmStoryActionSetItem[];
  columns?: 2 | 3;
  autoCycleMs?: number;
  mediaSide?: 'left' | 'right';
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  testId?: string;
}
